import { z } from 'zod';
import { pricingTierSchema } from '../PricingModelDetails.js';

// ============================================================================
// Enums
// ============================================================================
// TODO: refactor to follow zod schema paradigm
var PricingModel;
(function (PricingModel) {
    PricingModel["TIERED"] = "tiered";
    PricingModel["FLAT_FEE"] = "flatFee";
    PricingModel["FIXED"] = "fixed";
    PricingModel["LINEAR"] = "linear";
    PricingModel["LINEAR_HOURLY"] = "linearHourly";
    PricingModel["STEPPED"] = "stepped";
    PricingModel["VEHICLE_TYPE_CC"] = "vehicleTypeCC";
    PricingModel["STATE_BASED_FEE"] = "stateBasedFee";
    PricingModel["STAR_EV_DELIVERY_ESTIMATE"] = "starEvDeliveryEstimate";
    PricingModel["EKHO_PROCESSING_FEE"] = "ekhoProcessingFee";
})(PricingModel || (PricingModel = {}));
const pricingModelValues = [
    PricingModel.TIERED,
    PricingModel.FLAT_FEE,
    PricingModel.FIXED,
    PricingModel.LINEAR,
    PricingModel.LINEAR_HOURLY,
    PricingModel.STEPPED,
    PricingModel.VEHICLE_TYPE_CC,
    PricingModel.STATE_BASED_FEE,
    PricingModel.STAR_EV_DELIVERY_ESTIMATE,
    PricingModel.EKHO_PROCESSING_FEE,
];
const PricingModelSchema = z.enum(pricingModelValues);
// ============================================================================
// Base Types & Schemas
// ============================================================================
const ccPriceRangeSchema = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    price: z.number(),
});
// ============================================================================
// Pricing Model Schemas
// ============================================================================
// TIERED
const servicesProviderPricingTieredSchema = z.object({
    model: z.literal(PricingModel.TIERED),
    modelDetails: z.object({
        tiers: z.array(pricingTierSchema).optional(),
        tierResetCadence: z.enum(['annual', 'never']).optional(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// FLAT FEE
const servicesProviderPricingFlatFeeSchema = z.object({
    model: z.literal(PricingModel.FLAT_FEE),
    modelDetails: z.object({
        flatFee: z.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// FIXED
const servicesProviderPricingFixedSchema = z.object({
    model: z.literal(PricingModel.FIXED),
    modelDetails: z.object({
        fixedPercentageFee: z.number(), // Fixed percentage fee as a decimal between 0 and 1 (e.g., 0.05 for 5%)
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// LINEAR
const servicesProviderPricingLinearSchema = z.object({
    model: z.literal(PricingModel.LINEAR),
    modelDetails: z.object({
        pricePerMile: z.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// LINEAR HOURLY
const servicesProviderPricingLinearHourlySchema = z.object({
    model: z.literal(PricingModel.LINEAR_HOURLY),
    modelDetails: z.object({
        pricePerHour: z.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// STEPPED
const servicesProviderPricingSteppedSchema = z.object({
    model: z.literal(PricingModel.STEPPED),
    modelDetails: z.object({
        baseDistance: z.number().optional(),
        basePrice: z.number().optional(),
        pricePerMileAfter: z.number().optional(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// VEHICLE TYPE CC
const vehicleTypeCCModelDetailsSchema = z
    .object({
    default: z.object({
        flatFee: z.number(),
        priceRanges: z.array(ccPriceRangeSchema).optional(),
    }),
})
    .catchall(z.object({
    flatFee: z.number(),
    priceRanges: z.array(ccPriceRangeSchema).optional(),
}));
const servicesProviderPricingVehicleTypeCCSchema = z.object({
    model: z.literal(PricingModel.VEHICLE_TYPE_CC),
    modelDetails: vehicleTypeCCModelDetailsSchema,
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// STATE BASED FEE
const servicesProviderPricingStateBasedFeeSchema = z.object({
    model: z.literal(PricingModel.STATE_BASED_FEE),
    modelDetails: z.object({
        defaultAmount: z.number(),
        stateData: z
            .record(z.object({
            amount: z.number(),
        }))
            .optional(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// STAR EV DELIVERY ESTIMATE
const servicesProviderPricingStarEvDeliveryEstimateSchema = z.object({
    model: z.literal(PricingModel.STAR_EV_DELIVERY_ESTIMATE),
    modelDetails: z.object({
        defaultAmount: z.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// ============================================================================
// Special Schemas
// ============================================================================
// Code-only type (used for services that rely on base prices that ARE NOT the vehicle retail price, like coverage fees)
const servicesProviderPricingCodeOnlySchema = z.object({
    feeDescription: z.string().optional(),
    code: z.string().optional(), // code required to apply in Checkout // TODO MAKE THIS REQUIRED !!! @david promises he will
});
// ============================================================================
// DTO Schemas (with .coerce.number for form/API input)
// ============================================================================
// Code-only DTO (same as core since no numeric fields)
const servicesProviderPricingCodeOnlyDtoSchema = servicesProviderPricingCodeOnlySchema;
const pricingTierDtoSchema = z.object({
    vehicleMin: z.coerce.number(),
    vehicleMax: z.coerce.number(),
    percentageFee: z.coerce.number(),
});
const ccPriceRangeDtoSchema = z.object({
    min: z.coerce.number().optional(),
    max: z.coerce.number().optional(),
    price: z.coerce.number(),
});
// ============================================================================
// DTO Pricing Model Schemas
// ============================================================================
// TIERED
const servicesProviderPricingTieredDtoSchema = z.object({
    model: z.literal(PricingModel.TIERED),
    modelDetails: z.object({
        tiers: z.array(pricingTierDtoSchema).optional(),
        tierResetCadence: z.enum(['annual', 'never']).optional(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// FLAT FEE
const servicesProviderPricingFlatFeeDtoSchema = z.object({
    model: z.literal(PricingModel.FLAT_FEE),
    modelDetails: z.object({
        flatFee: z.coerce.number().min(0),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// FIXED
const servicesProviderPricingFixedDtoSchema = z.object({
    model: z.literal(PricingModel.FIXED),
    modelDetails: z.object({
        fixedPercentageFee: z.coerce.number().min(0).max(1), // Fixed percentage fee as a decimal between 0 and 1 (e.g., 0.05 for 5%)
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// LINEAR
const servicesProviderPricingLinearDtoSchema = z.object({
    model: z.literal(PricingModel.LINEAR),
    modelDetails: z.object({
        pricePerMile: z.coerce.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// LINEAR HOURLY
const servicesProviderPricingLinearHourlyDtoSchema = z.object({
    model: z.literal(PricingModel.LINEAR_HOURLY),
    modelDetails: z.object({
        pricePerHour: z.coerce.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// STEPPED
const servicesProviderPricingSteppedDtoSchema = z.object({
    model: z.literal(PricingModel.STEPPED),
    modelDetails: z.object({
        baseDistance: z.coerce.number().optional(),
        basePrice: z.coerce.number().optional(),
        pricePerMileAfter: z.coerce.number().optional(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// VEHICLE TYPE CC
const vehicleTypeCCModelDetailsDtoSchema = z
    .object({
    default: z.object({
        flatFee: z.coerce.number(),
        priceRanges: z.array(ccPriceRangeDtoSchema).optional(),
    }),
})
    .catchall(z.object({
    flatFee: z.coerce.number(),
    priceRanges: z.array(ccPriceRangeDtoSchema).optional(),
}));
const servicesProviderPricingVehicleTypeCCDtoSchema = z.object({
    model: z.literal(PricingModel.VEHICLE_TYPE_CC),
    modelDetails: vehicleTypeCCModelDetailsDtoSchema,
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// STATE BASED FEE
const servicesProviderPricingStateBasedFeeDtoSchema = z.object({
    model: z.literal(PricingModel.STATE_BASED_FEE),
    modelDetails: z.object({
        defaultAmount: z.coerce.number(),
        stateData: z
            .record(z.object({
            amount: z.coerce.number(),
        }))
            .optional(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// STAR EV DELIVERY ESTIMATE
const servicesProviderPricingStarEvDeliveryEstimateDtoSchema = z.object({
    model: z.literal(PricingModel.STAR_EV_DELIVERY_ESTIMATE),
    modelDetails: z.object({
        defaultAmount: z.coerce.number(),
    }),
    feeDescription: z.string().optional(),
    code: z.string().optional(),
});
// ============================================================================
// Generic Union Types
// ============================================================================
const servicesProviderPricingSchema = z.discriminatedUnion('model', [
    servicesProviderPricingTieredSchema,
    servicesProviderPricingFlatFeeSchema,
    servicesProviderPricingFixedSchema,
    servicesProviderPricingLinearSchema,
    servicesProviderPricingLinearHourlySchema,
    servicesProviderPricingSteppedSchema,
    servicesProviderPricingVehicleTypeCCSchema,
    servicesProviderPricingStateBasedFeeSchema,
    servicesProviderPricingStarEvDeliveryEstimateSchema,
]);
// DTO version
const servicesProviderPricingDtoSchema = z.discriminatedUnion('model', [
    servicesProviderPricingTieredDtoSchema,
    servicesProviderPricingFlatFeeDtoSchema,
    servicesProviderPricingFixedDtoSchema,
    servicesProviderPricingLinearDtoSchema,
    servicesProviderPricingLinearHourlyDtoSchema,
    servicesProviderPricingSteppedDtoSchema,
    servicesProviderPricingVehicleTypeCCDtoSchema,
    servicesProviderPricingStateBasedFeeDtoSchema,
    servicesProviderPricingStarEvDeliveryEstimateDtoSchema,
]);

export { PricingModel, PricingModelSchema, ccPriceRangeDtoSchema, ccPriceRangeSchema, pricingModelValues, pricingTierDtoSchema, servicesProviderPricingCodeOnlyDtoSchema, servicesProviderPricingCodeOnlySchema, servicesProviderPricingDtoSchema, servicesProviderPricingFixedDtoSchema, servicesProviderPricingFixedSchema, servicesProviderPricingFlatFeeDtoSchema, servicesProviderPricingFlatFeeSchema, servicesProviderPricingLinearDtoSchema, servicesProviderPricingLinearHourlyDtoSchema, servicesProviderPricingLinearHourlySchema, servicesProviderPricingLinearSchema, servicesProviderPricingSchema, servicesProviderPricingStarEvDeliveryEstimateDtoSchema, servicesProviderPricingStarEvDeliveryEstimateSchema, servicesProviderPricingStateBasedFeeDtoSchema, servicesProviderPricingStateBasedFeeSchema, servicesProviderPricingSteppedDtoSchema, servicesProviderPricingSteppedSchema, servicesProviderPricingTieredDtoSchema, servicesProviderPricingTieredSchema, servicesProviderPricingVehicleTypeCCDtoSchema, servicesProviderPricingVehicleTypeCCSchema };
//# sourceMappingURL=ServicesProviderPricing.js.map
